import React, { useState, useEffect, useMemo } from "react";
import {
  Input,
  Row,
  Card,
  Table,
  Spin,
  Button,
  Space,
  Form,
  Pagination,
} from "antd";
import { ArrowDownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import { BOM } from "../../../services/definedMethod";

// ── Dummy data (5 records) ─────────────────────────────────────────────────
const DUMMY_SETTLEMENTS = [
  {
    id: 1,
    tradeId: 1,
    settlementDate: "2026-02-24",
    cashAmount: 49_250_000.00,
    securityFaceValue: 50_000_000.00,
    status: "CONFIRMED",
    confirmedAt: "2026-02-24T10:30:00",
    externalReference: "SETTLE-2026-001",
  },
  {
    id: 2,
    tradeId: 2,
    settlementDate: "2026-03-03",
    cashAmount: 73_350_000.00,
    securityFaceValue: 75_000_000.00,
    status: "INSTRUCTED",
    confirmedAt: null,
    externalReference: "SETTLE-2026-002",
  },
  {
    id: 3,
    tradeId: 3,
    settlementDate: "2026-03-10",
    cashAmount: 96_500_000.00,
    securityFaceValue: 100_000_000.00,
    status: "PENDING",
    confirmedAt: null,
    externalReference: "SETTLE-2026-003",
  },
  {
    id: 4,
    tradeId: 4,
    settlementDate: "2026-03-17",
    cashAmount: 198_200_000.00,
    securityFaceValue: 200_000_000.00,
    status: "FAILED",
    confirmedAt: null,
    externalReference: "SETTLE-2026-004",
  },
  {
    id: 5,
    tradeId: 5,
    settlementDate: "2026-03-24",
    cashAmount: 28_500_000.00,
    securityFaceValue: 30_000_000.00,
    status: "CONFIRMED",
    confirmedAt: "2026-03-24T14:15:00",
    externalReference: "SETTLE-2026-005",
  },
];

// ── Status badge ───────────────────────────────────────────────────────────
const STATUS_COLORS = {
  CONFIRMED:  { bg: "#ECFDF3", color: "#027A48" },
  INSTRUCTED: { bg: "#EFF8FF", color: "#175CD3" },
  PENDING:    { bg: "#FFFAEB", color: "#B54708" },
  FAILED:     { bg: "#FEF3F2", color: "#B42318" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 99, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color }}>
      {status}
    </span>
  );
};

const fmt = (val) =>
  val != null ? val.toLocaleString("en-NG", { minimumFractionDigits: 2 }) : "—";

// ── Component ──────────────────────────────────────────────────────────────
const ManageSettlement = (props) => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const [form]    = Form.useForm();
  const { rightBtn, rightAction } = props;

  const [currentPage,  setCurrentPage]  = useState(1);
  const [selectedRow,  setSelectedRow]  = useState([]);
  const [searchTerm,   setSearchTerm]   = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [loading,      setLoading]      = useState(false);

  const pageSize = 10;

  const columns = [
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Settlement Date <ArrowDownOutlined style={{ fontSize: 14, color: "gray" }} />
        </div>
      ),
      dataIndex: "settlementDate",
      sorter: (a, b) => a.settlementDate.localeCompare(b.settlementDate),
    },
    { title: "Trade ID",           dataIndex: "tradeId" },
    { title: "External Reference", dataIndex: "externalReference" },
    { title: "Cash Amount",        dataIndex: "cashAmount",        render: fmt },
    { title: "Security Face Value",dataIndex: "securityFaceValue", render: fmt },
    {
      title: "Confirmed At",
      dataIndex: "confirmedAt",
      render: (val) => val ? new Date(val).toLocaleString("en-NG") : "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val) => <StatusBadge status={val} />,
    },
  ];

  useEffect(() => { getSettlementList(); }, []);

  useEffect(() => {
    if (BOM.HasPermission?.("CREATE_SETTLEMENT")) rightAction(cnt.RIGHT_ADD_BTN);
  }, []);

  const getSettlementList = () => {
    setLoading(true);
    // ── Swap for real API when ready ──
    // BOM.FetchReqAction({ session }, apiConfig.GetSettlements, (err, res) => { ... });
    setTimeout(() => {
      setOriginalList(DUMMY_SETTLEMENTS.map((el) => ({ key: el.id, ...el })));
      setSelectedRow([]);
      setCurrentPage(1);
      setLoading(false);
    }, 400);
  };

  const handleSearchInTable = (e) => {
    setSearchTerm(e.target.value || "");
    setCurrentPage(1);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;
    const q = searchTerm.toLowerCase();
    return originalList.filter(
      (item) =>
        item.externalReference?.toLowerCase().includes(q) ||
        item.status?.toLowerCase().includes(q) ||
        item.settlementDate?.toLowerCase().includes(q) ||
        String(item.tradeId).includes(q)
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const itemRender = (_, type, el) => {
    if (type === "prev" || type === "next") return;
    return el;
  };

  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      if (BOM.HasPermission?.("CREATE_SETTLEMENT")) dispatch({ type: cnt.RIGHT_ADD_BTN });
      return;
    }
    setSelectedRow(selectedRows);
    if (BOM.HasPermission?.("READ_SETTLEMENT"))   dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    if (BOM.HasPermission?.("UPDATE_SETTLEMENT")) dispatch({ type: cnt.RIGHT_EDIT_BTN });
  };

  const handleOnView   = () => { if (rightBtn?.detailBtn === "primary") navigate("/view-settlement",  { state: selectedRow[0]?.id }); };
  const handleOnCreate = () => { if (rightBtn?.addBtn    === "primary") navigate("/add-settlement"); };
  const handleOnEdit   = () => { if (rightBtn?.editBtn   === "primary") navigate("/edit-settlement", { state: selectedRow[0]?.id }); };

  return (
    <MainLayout isEnableRightSidebar onView={handleOnView} onAdd={handleOnCreate} onEdit={handleOnEdit} pageItems={staticConfig.manageSettlement}>
      <TopContent pageTitle={labelConfig.settlement} createPageTitle={labelConfig.settlementListing} />

      <Card style={{ border: "1px solid #EAECF0", borderRadius: 20, marginTop: 12 }}>
        <Spin spinning={loading}>
          <Row style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Form form={form}>
              <Space size={12}>
                <Input
                  value={searchTerm}
                  onChange={handleSearchInTable}
                  placeholder="Search settlements…"
                  prefix={<SearchLg width={18} color="#667085" />}
                  style={{ width: 350, borderRadius: 10 }}
                />
              </Space>
            </Form>
          </Row>

          <Table
            rowSelection={{ type: "checkbox", hideSelectAll: true, onChange: onSelectItemAction }}
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
          />

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
            <Button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}>
              <ArrowLeft /> Previous
            </Button>
            <Pagination current={currentPage} total={filteredData.length} pageSize={pageSize} showSizeChanger={false} itemRender={itemRender} onChange={setCurrentPage} />
            <Button disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}>
              Next <ArrowRight />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({ rightBtn: state.RightSideBtn });
export default connect(mapStateToProps, { rightAction })(ManageSettlement);